import React from 'react';
import classNames from 'classnames';

import { LogoBrandNameIcon, LogoIcon } from '@/design-system';
import { SidebarDivider, SidebarMenu, SidebarMenuItem } from './';
import { usePathname } from 'next/navigation';
import { LanguageSwitcher } from '@/app/design-system/components/LanguageSwitcher';
import { SidebarUserMenu } from './SidebarUserMenu';

type UserData = {
  imageUrl?: string;
  fullName?: string;
};

export interface SidebarNavItem {
  label: string;
  href: string;
  icon: React.FC<{ className?: string }>;
  target?: '_self' | '_blank';
}

export interface SidebarProps {
  className?: string;
  navItems: SidebarNavItem[];
  navShortcutItems?: SidebarNavItem[];
  user?: UserData;
  /** Logout shown in user profile dropdown only (admin stays in shortcuts) */
  logoutItem?: { label: string; href: string };
}

const COLLAPSE_DELAY_MS = 600;
const EXPAND_DELAY_MS = 300;

export const Sidebar: React.FC<SidebarProps> = ({
  className,
  navItems = [],
  navShortcutItems = [],
  user,
  logoutItem,
}) => {
  const [collapsed, setCollapsed] = React.useState(true);
  const leaveTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const enterTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();
  const isActive = (href: string) => pathname === href;

  const clearLeaveTimeout = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  };

  const clearEnterTimeout = () => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
  };

  const onMouseEnter = () => {
    clearLeaveTimeout();
    enterTimeoutRef.current = setTimeout(() => setCollapsed(false), EXPAND_DELAY_MS);
  };

  const onMouseLeave = () => {
    clearEnterTimeout();
    leaveTimeoutRef.current = setTimeout(() => setCollapsed(true), COLLAPSE_DELAY_MS);
  };

  React.useEffect(
    () => () => {
      clearLeaveTimeout();
      clearEnterTimeout();
    },
    [],
  );

  const containerClasses = classNames(
    className,
    'bg-ds-sidebar py-6 px-4 text-ds-text-inverse-3 group/sidebar',
    'w-84 [&.collapsed]:w-20 transition-all duration-500 ease-in-out',
    '[&_span]:max-w-60 [&.collapsed_span]:max-w-0 [&_span]:transition-all [&_span]:duration-500 [&_span]:ease-in-out [&_span]:overflow-hidden [&_span]:block whitespace-nowrap',
    { collapsed: collapsed },
  );

  return (
    <div className={containerClasses} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className="flex flex-col h-full">
        <div className="flex shrink-0 justify-between group-[.collapsed]/sidebar:flex-col group-[.collapsed]/sidebar:gap-8">
          <div className="flex  gap-2.5 min-w-12 justify-start px-2.5">
            <LogoIcon className="shrink-0 group-[.collapsed]/sidebar:rotate-180 transition-transform duration-500 ease-in-out" />
            <LogoBrandNameIcon className="group-[.collapsed]/sidebar:scale-0 group-[.collapsed]/sidebar:w-0 scale-100 transition-all duration-500 ease-in-out" />
          </div>
        </div>
        <SidebarDivider className="my-8" />
        <div>
          <SidebarMenu>
            {navItems.map((item, _index) => (
              <SidebarMenuItem
                key={_index}
                icon={item.icon}
                href={item.href}
                active={isActive(item.href)}
              >
                {item.label}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </div>
        <div className="mt-auto shrink-0 relative w-full">
          <SidebarUserMenu user={user} logoutItem={logoutItem} />
          <SidebarDivider className="mt-5 mb-4" />
          <div className="py-1">
            <SidebarMenu>
              {navShortcutItems.map((item, _index) => (
                <SidebarMenuItem
                  key={_index}
                  icon={item.icon}
                  href={item.href}
                  active={isActive(item.href)}
                  target={item.target}
                >
                  {item.label}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
            <div>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
